import {
  CalendarIcon,
  PersonIcon,
  SewingPinIcon,
  UpdateIcon,
  VideoIcon,
} from "@radix-ui/react-icons";
import {
  ApiError,
  Auth0AccountRole,
  MeetingsService,
  SessionFrequency,
} from "client/openapi";
import type { FamilyMember } from "client/openapi/models/FamilyMember";
import type { MeetingRequest } from "client/openapi/models/MeetingRequest";
import { PeopleService } from "client/openapi/services/PeopleService";
import { Dialog, DialogContent } from "components/Dialog";
import IconBubble, { BubbleTone } from "components/IconBubble";
import moment from "moment";
import { ReactNode, useCallback, useEffect, useState } from "react";
import { generateInitials } from "util/generateInitials";
import Notifications from "util/notifications";
import { announceMeetingsChanged } from "util/meetingsChanged";
import { personName } from "./person";

const EVERY: Record<SessionFrequency, string> = {
  [SessionFrequency.SINGLE]: "One time",
  [SessionFrequency.EVERY_7_DAYS]: "Every week",
  [SessionFrequency.EVERY_14_DAYS]: "Every 2 weeks",
  [SessionFrequency.EVERY_21_DAYS]: "Every 3 weeks",
  [SessionFrequency.EVERY_28_DAYS]: "Every 4 weeks",
};

/** Who booked it, by the role that did. */
const ASKED_BY: Record<Auth0AccountRole, string> = {
  [Auth0AccountRole.ME]: "Requested by the family",
  [Auth0AccountRole.PARENT]: "Requested by a parent",
  [Auth0AccountRole.ORG_ADMIN]: "Booked by an admin",
  [Auth0AccountRole.TADPOLE_ADMIN]: "Booked by an admin",
  [Auth0AccountRole.ORG_TUTOR]: "Booked by a tutor",
};

/** "1 hr", "45 min", "1 hr 30 min". */
function length(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return [hours && `${hours} hr`, rest && `${rest} min`]
    .filter(Boolean)
    .join(" ");
}

/**
 * When it meets: "Tue, Oct 27 · 8:30 – 9:30 pm · 1 hr". A series says
 * where it starts, which is where Accept and Decline answer from: "Starts
 * Tue, Oct 6", or once part of it has passed, "Started Tue, Oct 6 · next
 * Tue, Oct 20".
 */
function when(request: MeetingRequest): string {
  const start = moment(request.start);
  const end = start.clone().add(request.duration, "minutes");
  const sameHalf = start.format("a") === end.format("a");
  const times = `${start.format(sameHalf ? "h:mm" : "h:mm a")} – ${end.format(
    "h:mm a"
  )}`;
  const day = (at: string) => moment(at).format("ddd, MMM D");
  const first = moment(request.series_start);
  const days =
    request.frequency === SessionFrequency.SINGLE
      ? [day(request.start)]
      : first.isBefore(moment())
      ? [`Started ${day(request.series_start)}`, `next ${day(request.start)}`]
      : [`Starts ${day(request.series_start)}`];
  return [...days, times, length(request.duration)].join(" · ");
}

/** How it repeats: "One time", or "Every week on Thursdays, until Dec 17". */
function repeats(request: MeetingRequest): string {
  if (request.frequency === SessionFrequency.SINGLE) {
    return EVERY[request.frequency];
  }
  const day = moment(request.start).format("dddd");
  const until =
    request.ends_on_or_before &&
    `, until ${moment(request.ends_on_or_before).format("MMM D")}`;
  return `${EVERY[request.frequency]} on ${day}s${until || ""}`;
}

/** "Maria Angelo · Parent: Omar Haddad". */
function family(request: MeetingRequest): string {
  const names = (people: FamilyMember[] = []) =>
    people.map(personName).filter(Boolean).join(", ");
  const students = names(request.students);
  const parents = names(request.parents);
  const label = (request.parents?.length ?? 0) > 1 ? "Parents" : "Parent";
  return [students, parents && `${label}: ${parents}`]
    .filter(Boolean)
    .join(" · ");
}

/** A fact about the request, labeled by its icon in a tinted bubble. */
function Fact({
  icon,
  tone,
  children,
}: {
  icon: ReactNode;
  tone: BubbleTone;
  children: ReactNode;
}) {
  return (
    <li className="people-request-card__fact">
      <IconBubble tone={tone} small>
        {icon}
      </IconBubble>
      <span>{children}</span>
    </li>
  );
}

/**
 * The meeting requests window: each request once, a series as one card,
 * with what was asked (when, how often, where, for whom, any note) and
 * Accept and Decline for each tutor still to answer. An admin answers for
 * the tutor through the tutor's own endpoint, which answers the whole
 * series, so the family's messages go out as usual; whoever answers second
 * is told it was already answered. Either way onAnswered reloads.
 */
export function MeetingRequestsWindow({
  requests,
  onAnswered,
  onClose,
}: {
  requests: MeetingRequest[];
  onAnswered: () => void;
  onClose: () => void;
}) {
  const [answering, setAnswering] = useState(false);
  // One card per session, with each tutor who hasn't answered it.
  const sessions = Array.from(
    requests
      .reduce((bySession, request) => {
        const tutors = bySession.get(request.session_id) ?? [];
        return bySession.set(request.session_id, [...tutors, request]);
      }, new Map<number, MeetingRequest[]>())
      .values()
  );
  // Whom they are for, each once.
  const forWhom = Array.from(
    new Set(
      requests.flatMap((request) =>
        (request.students ?? []).map(personName).filter(Boolean)
      )
    )
  );

  function answer(request: MeetingRequest, accept: boolean) {
    const tutor = personName(request.tutor) || "the tutor";
    setAnswering(true);
    MeetingsService.acceptOrRejectMeetingAsTutor({
      tutorId: request.tutor.id,
      sessionId: request.session_id,
      accept,
    })
      .then(() => {
        announceMeetingsChanged();
        Notifications.success(
          accept ? `Accepted for ${tutor}.` : `Declined for ${tutor}.`
        );
      })
      .catch((error: ApiError) =>
        Notifications.error(
          error?.status === 409
            ? error.body?.detail ?? "This meeting request was already answered."
            : "Could not answer the request. Try again."
        )
      )
      .finally(() => {
        setAnswering(false);
        onAnswered();
      });
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="dialog-content people-request-window">
        <div className="people-request-window__header">
          <span className="people-request-window__badge" aria-hidden>
            <CalendarIcon />
          </span>
          <div>
            <h2 className="people-request-window__title">
              {sessions.length > 1
                ? `${sessions.length} meeting requests`
                : "Meeting request"}
            </h2>
            {forWhom.length > 0 && (
              <p className="people-request-window__for">
                For {forWhom.join(" and ")}
              </p>
            )}
          </div>
        </div>
        {sessions.length === 0 ? (
          <p className="mt-4 text-sm text-gray-700">
            That meeting request was already answered.
          </p>
        ) : (
          <ul className="people-request-cards">
            {sessions.map((tutors) => {
              const request = tutors[0];
              return (
                <li key={request.session_id} className="people-request-card">
                  <p className="people-request-card__name">{request.name}</p>
                  <p className="people-request-card__asked">
                    {ASKED_BY[request.requested_by]}
                  </p>
                  <ul className="people-request-card__facts">
                    <Fact icon={<CalendarIcon />} tone={BubbleTone.ORANGE}>
                      {when(request)}
                    </Fact>
                    <Fact icon={<UpdateIcon />} tone={BubbleTone.SKY}>
                      {repeats(request)}
                    </Fact>
                    <Fact
                      icon={request.online ? <VideoIcon /> : <SewingPinIcon />}
                      tone={BubbleTone.INDIGO}
                    >
                      {request.online && (
                        <span className="sr-only">Online: </span>
                      )}
                      {request.place || "No place set"}
                    </Fact>
                    {family(request) && (
                      <Fact icon={<PersonIcon />} tone={BubbleTone.GREEN}>
                        {family(request)}
                      </Fact>
                    )}
                  </ul>
                  {request.note && (
                    <p className="people-request-card__note">{request.note}</p>
                  )}
                  {tutors.map((request) => {
                    const tutor = personName(request.tutor) || "A tutor";
                    return (
                      <div
                        key={request.tutor.id}
                        className="people-request-card__tutor"
                      >
                        <span className="people-request-card__who">
                          <span
                            className="people-request-card__initials"
                            aria-hidden
                          >
                            {generateInitials(tutor)}
                          </span>
                          With {tutor}
                        </span>
                        <span className="people-request-card__answers">
                          <button
                            type="button"
                            className="people-request-card__answer"
                            aria-label={`Decline ${request.name} for ${tutor}`}
                            disabled={answering}
                            onClick={() => answer(request, false)}
                          >
                            Decline
                          </button>
                          <button
                            type="button"
                            className="people-request-card__answer people-request-card__answer--accept"
                            aria-label={`Accept ${request.name} for ${tutor}`}
                            disabled={answering}
                            onClick={() => answer(request, true)}
                          >
                            Accept
                          </button>
                        </span>
                      </div>
                    );
                  })}
                </li>
              );
            })}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * One session's open requests, loaded by its id, in the requests window:
 * the admin dashboard's ?request=<session> link, and the bell's request
 * events. After an answer it reloads, so it says when all are answered.
 */
export function SessionRequestsDialog({
  orgId,
  sessionId,
  onClose,
  onAnswered,
}: {
  orgId: number;
  sessionId: number;
  onClose: () => void;
  /** A tutor's answer was sent (the calendar and People can reload). */
  onAnswered?: () => void;
}) {
  const [requests, setRequests] = useState<MeetingRequest[]>();
  const load = useCallback(() => {
    PeopleService.getSessionRequests({ orgId, sessionId })
      .then(setRequests)
      .catch(() => {
        Notifications.error("Could not open the meeting request.");
        onClose();
      });
  }, [orgId, sessionId, onClose]);
  useEffect(load, [load]);

  if (!requests) return null;
  return (
    <MeetingRequestsWindow
      requests={requests}
      onAnswered={() => {
        load();
        onAnswered?.();
      }}
      onClose={onClose}
    />
  );
}

/**
 * A student's open meeting requests as one chip ("Meeting requests (2)",
 * counting sessions), opening the requests window.
 */
export default function MeetingRequests({
  requests,
  onAnswered,
}: {
  requests: MeetingRequest[];
  onAnswered: () => void;
}) {
  const [open, setOpen] = useState(false);
  const count = new Set(requests.map((request) => request.session_id)).size;

  return (
    <>
      <button
        type="button"
        className="people-chip people-chip--request"
        onClick={() => setOpen(true)}
      >
        {count === 1 ? "Meeting request" : `Meeting requests (${count})`}
      </button>
      {open && (
        <MeetingRequestsWindow
          requests={requests}
          onAnswered={onAnswered}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
